import { useEffect, useMemo, useRef, useState } from 'react';
import type { PlayerPublic } from '../../../shared/types';
import type { Cell, Color } from '../../../shared/board';
import { HOME_CENTER, HOME_LANE, RING, YARD_SLOTS, isSafeRingIndex } from '../../../shared/board';
import { colorLabel, colors, ringForToken } from './boardUi';
import { playHome, playHop } from '../sound';

const CELL = 40;
/** Milliseconds per box a moving token hops through. */
const STEP_MS = 230;

interface BoardProps {
  players: PlayerPublic[];
  myColor: Color | null;
  movableTokens: number[];
  currentColor: Color | null;
  onMoveToken: (tokenIndex: number) => void;
}

interface TokenDot {
  color: Color;
  index: number;
  key: string;
  movable: boolean;
}

interface TokenMotion {
  path: Cell[];
  startedAt: number;
}

function tokenKey(color: Color, index: number): string {
  return `${color}:${index}`;
}

/** The on-board cell for a distance, mirroring tokenPose (used for hop paths). */
function cellFor(color: Color, d: number): Cell {
  if (d >= 1 && d <= 51) return RING[ringForToken(color, d)];
  if (d >= 52 && d <= 56) return HOME_LANE[color][d - 52];
  return HOME_CENTER;
}

function tokensByColor(players: PlayerPublic[]): Record<Color, number[]> {
  const m = {} as Record<Color, number[]>;
  for (const p of players) m[p.color] = p.tokens;
  return m;
}

export default function Board({ players, myColor, movableTokens, currentColor, onMoveToken }: BoardProps) {
  // Tokens currently hopping across the board; driven by a rAF clock.
  const [motion, setMotion] = useState<Record<string, TokenMotion>>({});
  const motionRef = useRef(motion);
  motionRef.current = motion;
  const nowRef = useRef(performance.now());
  const prevTokensRef = useRef<Record<Color, number[]> | null>(null);
  const announcedHomeRef = useRef<Set<string>>(new Set());
  const hopTicksRef = useRef<Map<string, { startedAt: number; last: number }>>(new Map());

  const currentByColor = useMemo(() => tokensByColor(players), [players]);

  // Detect moved tokens at render time (compare against the previous render)
  // and kick off their hop — no effect needed, so there's no 1-frame flash.
  if (prevTokensRef.current) {
    const prev = prevTokensRef.current;
    const starts: Record<string, TokenMotion> = {};
    for (const p of players) {
      const next = p.tokens;
      const old = prev[p.color];
      if (!old || old.length !== next.length) continue;
      next.forEach((d, i) => {
        if (d > old[i] && d >= 1 && d <= 57) {
          const path: Cell[] = [];
          // Start the hop where the token actually sits (its yard slot when
          // launching), not on the next box.
          if (old[i] === 0) {
            const slot = YARD_SLOTS[p.color][i % YARD_SLOTS[p.color].length];
            path.push({ x: slot.x, y: slot.y });
          } else {
            path.push(cellFor(p.color, old[i]));
          }
          for (let k = old[i] + 1; k <= d; k++) path.push(cellFor(p.color, k));
          starts[tokenKey(p.color, i)] = { path, startedAt: performance.now() };
        }
        if (d === 57 && old[i] !== 57) {
          const k = tokenKey(p.color, i);
          if (!announcedHomeRef.current.has(k)) {
            announcedHomeRef.current.add(k);
            playHome();
          }
        } else if (d !== 57) {
          announcedHomeRef.current.delete(tokenKey(p.color, i));
        }
      });
    }
    prevTokensRef.current = currentByColor;
    if (Object.keys(starts).length > 0) {
      setMotion((m) => ({ ...m, ...starts }));
    }
  } else {
    prevTokensRef.current = currentByColor;
  }

  // Smoothly advance the hop clock and retire finished hops. Re-renders each
// frame while a token is airborne so the path interpolates rather than
// teleporting between boxes.
  useEffect(() => {
    if (Object.keys(motionRef.current).length === 0) return;
    let raf = 0;
    const tick = () => {
      nowRef.current = performance.now();
      // Play a tick each time a moving token lands on the next box.
      const now = nowRef.current;
      for (const [k, hop] of Object.entries(motionRef.current)) {
        const box = Math.floor(Math.max(0, now - hop.startedAt) / STEP_MS);
        const rec = hopTicksRef.current.get(k);
        const last = !rec || rec.startedAt !== hop.startedAt ? -1 : rec.last;
        if (box > last && box < hop.path.length - 1) {
          hopTicksRef.current.set(k, { startedAt: hop.startedAt, last: box });
          playHop(box);
        }
      }
      setMotion((m) => {
        const next = { ...m };
        let changed = false;
        for (const [k, hop] of Object.entries(m)) {
          if (nowRef.current - hop.startedAt >= hop.path.length * STEP_MS) {
            delete next[k];
            changed = true;
          }
        }
        // Fresh object every frame so the render recomputes each hop's current
        // position from the clock (state-equality bail-outs would freeze it).
        return changed ? next : { ...m };
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [motion]);

  // Collect every visible token, grouped by position so tokens that share a
  // square (safe zones, yard, centre) fan out side by side. Hopping tokens are
  // excluded here and rendered separately on top, along their path.
  const visible: TokenDot[] = [];
  for (const p of players) {
    p.tokens.forEach((d, i) => {
      const key = tokenPose(p.color, d, i).key;
      if (motion[tokenKey(p.color, i)]) return;
      visible.push({ color: p.color, index: i, key, movable: myColor === p.color && movableTokens.includes(i) });
    });
  }
  const groups = new Map<string, TokenDot[]>();
  for (const t of visible) {
    const arr = groups.get(t.key) ?? [];
    arr.push(t);
    groups.set(t.key, arr);
  }

  return (
    <svg viewBox="0 0 600 600" className="h-full w-full drop-shadow-[0_24px_40px_-12px_rgba(0,0,0,0.65)]">
      <defs>
        {/* Token spheres — glossy marble shading per color, lit from top-left */}
        {colors.map((c) => (
          <radialGradient key={c} id={`ludo-token-${c}`} cx="0.36" cy="0.3" r="0.95" fx="0.3" fy="0.22">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="30%" stopColor={TOKEN_LIGHT[c]} />
            <stop offset="62%" stopColor={TOKEN[c]} />
            <stop offset="100%" stopColor={TOKEN_DARK[c]} />
          </radialGradient>
        ))}
        {/* Shared rim shade — darkens the far edge for sphere depth */}
        <radialGradient id="lud-token-shade" cx="0.28" cy="0.25" r="1.25">
          <stop offset="45%" stopColor="rgba(0,0,0,0)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.5)" />
        </radialGradient>

        {/* Yard washes */}
        {colors.map((c) => (
          <linearGradient key={c} id={`lud-yard-${c}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={YARD_TOP[c]} />
            <stop offset="100%" stopColor={YARD_BOTTOM[c]} />
          </linearGradient>
        ))}

        {/* Home lane tints */}
        {colors.map((c) => (
          <linearGradient key={c} id={`lud-lane-${c}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={LANE_TOP[c]} />
            <stop offset="100%" stopColor={LANE_BOTTOM[c]} />
          </linearGradient>
        ))}

        <radialGradient id="lud-wood" cx="0.5" cy="0.22" r="1.15">
          <stop offset="0%" stopColor="#45352a" />
          <stop offset="55%" stopColor="#33251a" />
          <stop offset="100%" stopColor="#221609" />
        </radialGradient>

        <radialGradient id="lud-board-glow" cx="0.5" cy="0.4" r="0.75">
          <stop offset="0%" stopColor="rgba(255,214,140,0.20)" />
          <stop offset="100%" stopColor="rgba(255,214,140,0)" />
        </radialGradient>

        <linearGradient id="lud-gold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ffe9a8" />
          <stop offset="45%" stopColor="#f5c23f" />
          <stop offset="100%" stopColor="#d99b1f" />
        </linearGradient>

        <radialGradient id="lud-center-medal" cx="0.35" cy="0.3" r="1">
          <stop offset="0%" stopColor="#fff3cf" />
          <stop offset="55%" stopColor="#ffd257" />
          <stop offset="100%" stopColor="#e0a529" />
        </radialGradient>

        {/* Safe-cell star glow */}
        {[9, 22, 35, 48].map((i) => (
          <radialGradient key={i} id={`lud-star-${i}`} cx="0.5" cy="0.5" r="0.8">
            <stop offset="0%" stopColor="rgba(255,214,110,0.85)" />
            <stop offset="100%" stopColor="rgba(255,214,110,0)" />
          </radialGradient>
        ))}

        <filter id="lud-shadow" x="-40%" y="-40%" width="180%" height="180%">
          <feDropShadow dx="0" dy="2.4" stdDeviation="2.2" floodColor="rgba(0,0,0,0.5)" />
        </filter>
      </defs>

      {/* ── Board base ── */}
      <rect x="4" y="4" width="592" height="592" rx="26" fill="url(#lud-wood)" />
      <rect x="4" y="4" width="592" height="592" rx="26" fill="url(#lud-board-glow)" />
      <rect x="4" y="4" width="592" height="592" rx="26" fill="none" stroke="rgba(244,196,110,0.6)" strokeWidth="4.5" />

      {/* Faint grid */}
      {Array.from({ length: 16 }, (_, i) => (
        <g key={i} stroke="rgba(255,255,255,0.028)">
          <line x1={i * CELL} y1={0} x2={i * CELL} y2={600} />
          <line x1={0} y1={i * CELL} x2={600} y2={i * CELL} />
        </g>
      ))}

      {/* ── Yards ── */}
      {colors.map((c) => {
        const o = YARD_ORIGIN[c];
        const isCurrent = c === currentColor;
        return (
          <g key={c}>
            <rect
              x={o.x * CELL + 4}
              y={o.y * CELL + 4}
              width={6 * CELL - 8}
              height={6 * CELL - 8}
              rx={isCurrent ? 18 : 16}
              fill={`url(#lud-yard-${c})`}
              stroke={isCurrent ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.35)'}
              strokeWidth={isCurrent ? 3 : 1.5}
              style={isCurrent ? { filter: 'drop-shadow(0 0 14px rgba(255,255,255,0.35))' } : { filter: 'drop-shadow(0 6px 10px rgba(0,0,0,0.35))' }}
            />
            {/* Inner bevel */}
            <rect
              x={o.x * CELL + 12}
              y={o.y * CELL + 12}
              width={6 * CELL - 24}
              height={6 * CELL - 24}
              rx={12}
              fill="none"
              stroke={isCurrent ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.18)'}
              strokeWidth={1.5}
              strokeDasharray="3 4"
            />
            <text
              x={o.x * CELL + 3 * CELL}
              y={o.y * CELL + 1.55 * CELL}
              textAnchor="middle"
              fontSize="15"
              fontWeight={800}
              fill="rgba(255,255,255,0.9)"
              style={{ fontFamily: 'Fredoka, ui-rounded, system-ui, sans-serif' }}
            >
              {colorLabel(c)}
            </text>
            {isCurrent && (
              <text
                x={o.x * CELL + 3 * CELL}
                y={o.y * CELL + 4.9 * CELL}
                textAnchor="middle"
                fontSize="10"
                fontWeight={800}
                fill="#fff"
                className="lud-pop"
                style={{ fontFamily: 'Fredoka, ui-rounded, system-ui, sans-serif', letterSpacing: '0.5px' }}
              >
                ● YOUR TURN
              </text>
            )}
          </g>
        );
      })}

      {/* ── Home lanes ── */}
      {colors.map((c) =>
        HOME_LANE[c].map((cell, i) => (
          <g key={`${c}-lane-${i}`}>
            <rect
              x={cell.x * CELL + 3.5}
              y={cell.y * CELL + 3.5}
              width={CELL - 7}
              height={CELL - 7}
              rx={9}
              fill={`url(#lud-lane-${c})`}
              stroke="rgba(255,255,255,0.75)"
              strokeWidth={1.4}
            />
            {/* Direction chevron toward the centre */}
            {arrowFor(c, cell, i)}
          </g>
        )),
      )}

      {/* ── Ring cells ── */}
      {RING.map((cell, i) => {
        const safe = isSafeRingIndex(i);
        const isStart = i % 13 === 1;
        return (
          <g key={i}>
            <rect
              x={cell.x * CELL + 1.5}
              y={cell.y * CELL + 1.5}
              width={CELL - 3}
              height={CELL - 3}
              rx={safe ? 12 : 9}
              fill={safe ? 'url(#lud-gold)' : '#f7efe0'}
              stroke={safe ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.6)'}
              strokeWidth={1.6}
              style={safe ? { filter: 'drop-shadow(0 0 10px rgba(255,190,70,0.55))' } : { filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.4))' }}
            />
            {safe && <circle cx={cell.x * CELL + CELL / 2} cy={cell.y * CELL + CELL / 2} r={13} fill={`url(#lud-star-${i})`} />}
            {safe && (
              <PathStar
                x={cell.x * CELL + CELL / 2}
                y={cell.y * CELL + CELL / 2}
                r={i % 13 === 1 ? 9 : 7}
                fill={i % 13 === 0 ? '#fff6dd' : '#ffffffd9'}
                opacity={0.95}
              />
            )}
            {isStart && (
              <circle
                cx={cell.x * CELL + CELL / 2}
                cy={cell.y * CELL + CELL / 2}
                r={7}
                fill={TOKEN[startCellOwner(i)]}
                stroke="rgba(255,255,255,0.95)"
                strokeWidth={2}
              />
            )}
          </g>
        );
      })}

      {/* ── Centre ── */}
      <g>
        <rect x={6 * CELL + 5} y={6 * CELL + 5} width={3 * CELL - 10} height={3 * CELL - 10} rx={16} fill="url(#lud-gold)" />
        <rect x={6 * CELL + 5} y={6 * CELL + 5} width={3 * CELL - 10} height={3 * CELL - 10} rx={16} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
        <path d={`M ${6.4 * CELL} ${7.5 * CELL} L ${7.5 * CELL} ${6.4 * CELL} L ${7.5 * CELL} ${7.5 * CELL} Z`} fill={CENTER_TINT.red} />
        <path d={`M ${7.5 * CELL} ${6.4 * CELL} L ${8.6 * CELL} ${7.5 * CELL} L ${7.5 * CELL} ${7.5 * CELL} Z`} fill={CENTER_TINT.green} />
        <path d={`M ${8.6 * CELL} ${7.5 * CELL} L ${7.5 * CELL} ${8.6 * CELL} L ${7.5 * CELL} ${7.5 * CELL} Z`} fill={CENTER_TINT.yellow} />
        <path d={`M ${7.5 * CELL} ${8.6 * CELL} L ${6.4 * CELL} ${7.5 * CELL} L ${7.5 * CELL} ${7.5 * CELL} Z`} fill={CENTER_TINT.blue} />
        <circle cx={7.5 * CELL} cy={7.5 * CELL} r={17} fill="url(#lud-center-medal)" stroke="rgba(255,255,255,0.9)" strokeWidth={2} />
        <PathStar x={7.5 * CELL} y={7.5 * CELL} r={11} fill="#fff8e8" />
        <circle cx={7.5 * CELL} cy={7.5 * CELL} r={17} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={1} className="lud-spin-slow" />
      </g>

      {/* ── Tokens ── */}
      {[...groups.entries()].map(([key, list]) => {
        const n = list.length;
        return (
          <g key={key}>
            {list.map((t, k) => {
              const x = poseX(key, k, n);
              const y = poseY(key, k, n);
              return <TokenShape key={`${t.color}-${t.index}`} x={x} y={y} color={t.color} index={t.index} movable={t.movable} onClick={() => t.movable && onMoveToken(t.index)} />;
            })}
          </g>
        );
      })}

      {/* Hopping tokens — glide across every box along their path, landing with a bounce */}
      {Object.entries(motion).map(([key, hop]) => {
        const [color, indexS] = key.split(':');
        const c = color as Color;
        const idx = Number(indexS);
        const seg = Math.min(Math.max(0, performance.now() - hop.startedAt) / STEP_MS, hop.path.length - 1);
        const i = Math.floor(seg);
        const t = seg - i;
        const from = hop.path[i];
        const to = hop.path[Math.min(i + 1, hop.path.length - 1)];
        const x = (from.x + (to.x - from.x) * t) * CELL + CELL / 2;
        const y = (from.y + (to.y - from.y) * t) * CELL + CELL / 2;
        return (
          <g key={key} className="lud-hop" style={{ animationDuration: `${STEP_MS}ms` }}>
            <TokenShape x={x} y={y} color={c} index={idx} movable={false} onClick={() => {}} />
          </g>
        );
      })}
    </svg>
  );
}

function TokenShape({
  x,
  y,
  color,
  index,
  movable,
  onClick,
}: {
  x: number;
  y: number;
  color: Color;
  index: number;
  movable: boolean;
  onClick: () => void;
}) {
  const shadow = (
    <ellipse cx={x} cy={y + 14} rx={15.5} ry={4.4} fill="rgba(0,0,0,0.45)" style={{ filter: 'blur(1.7px)' }} />
  );
  const body = (
    <g filter="url(#lud-shadow)">
      {/* Glossy 3D marble */}
      <circle cx={x} cy={y} r={15} fill={`url(#ludo-token-${color})`} stroke={TOKEN_EDGE[color]} strokeWidth={1.1} />
      {/* Rim shade for sphere depth (light from the top-left) */}
      <circle cx={x} cy={y} r={15} fill="url(#lud-token-shade)" />
      {/* Soft secondary glare */}
      <ellipse cx={x - 3.6} cy={y - 4.6} rx={7.6} ry={4.8} fill="rgba(255,255,255,0.28)" transform={`rotate(-26 ${x - 3.6} ${y - 4.6})`} />
      {/* Specular hotspot */}
      <circle cx={x - 5} cy={y - 5.8} r={3.7} fill="rgba(255,255,255,0.92)" style={{ filter: 'blur(0.7px)' }} />
      <text x={x} y={y + 5} textAnchor="middle" fontSize="13" fontWeight={800} fill="#ffffff" style={{ filter: 'drop-shadow(0 1.2px 1.2px rgba(0,0,0,0.45))' }}>
        {index + 1}
      </text>
    </g>
  );

  if (!movable) {
    return (
      <g>
        {shadow}
        {body}
      </g>
    );
  }

  return (
    <g className="cursor-pointer" onClick={onClick} role="button" aria-label={`Move token ${index + 1}`}>
      {shadow}
      <g className="lud-movable">{body}</g>
    </g>
  );
}

function PathStar({ x, y, r, fill, opacity = 1 }: { x: number; y: number; r: number; fill: string; opacity?: number }) {
  const points = Array.from({ length: 10 }, (_, i) => {
    const rad = i % 2 === 0 ? r : r * 0.42;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    return `${x + rad * Math.cos(a)},${y + rad * Math.sin(a)}`;
  }).join(' ');
  return <polygon points={points} fill={fill} opacity={opacity} />;
}

/** Tiny chevron on a home-lane cell pointing toward the centre. */
function arrowFor(c: Color, cell: { x: number; y: number }, i: number) {
  const cx = cell.x * CELL + CELL / 2;
  const cy = cell.y * CELL + CELL / 2;
  const pts: Record<Color, string> = {
    red: `${cx - 5},${cy - 5} ${cx + 5},${cy} ${cx - 5},${cy + 5}`,
    green: `${cx - 5},${cy - 5} ${cx},${cy + 5} ${cx + 5},${cy - 5}`,
    yellow: `${cx + 5},${cy - 5} ${cx - 5},${cy} ${cx + 5},${cy + 5}`,
    blue: `${cx - 5},${cy + 5} ${cx},${cy - 5} ${cx + 5},${cy + 5}`,
  };
  return <polygon points={pts[c]} fill="rgba(255,255,255,0.55)" />;
}

// ── Token pose helpers ───────────────────────────────────────────────────────

function tokenPose(color: Color, d: number, index: number): { key: string; x: number; y: number } {
  if (d === 0) {
    const slot = YARD_SLOTS[color][index % YARD_SLOTS[color].length];
    const cx = slot.x * CELL + CELL / 2;
    const cy = slot.y * CELL + CELL / 2;
    return { key: `${cx},${cy}`, x: cx, y: cy };
  }
  if (d >= 1 && d <= 51) {
    const cell = RING[ringForToken(color, d)];
    const cx = cell.x * CELL + CELL / 2;
    const cy = cell.y * CELL + CELL / 2;
    return { key: `${cx},${cy}`, x: cx, y: cy };
  }
  if (d >= 52 && d <= 56) {
    const cell = HOME_LANE[color][d - 52];
    const cx = cell.x * CELL + CELL / 2;
    const cy = cell.y * CELL + CELL / 2;
    return { key: `${cx},${cy}`, x: cx, y: cy };
  }
  const cx = HOME_CENTER.x * CELL + CELL / 2;
  const cy = HOME_CENTER.y * CELL + CELL / 2;
  return { key: `${cx},${cy}`, x: cx, y: cy };
}

const YARD_ORIGIN: Record<Color, { x: number; y: number }> = {
  red: { x: 0, y: 0 },
  green: { x: 9, y: 0 },
  yellow: { x: 9, y: 9 },
  blue: { x: 0, y: 9 },
};

function poseX(key: string, k: number, n: number): number {
  const [x] = key.split(',').map(Number);
  return x + (k - (n - 1) / 2) * (n > 1 ? 17 : 0);
}
function poseY(key: string, k: number, n: number): number {
  const [, y] = key.split(',').map(Number);
  return y + (k - (n - 1) / 2) * (n > 1 ? 17 : 0);
}

function startCellOwner(i: number): Color {
  if (i === 1) return 'red';
  if (i === 14) return 'green';
  if (i === 27) return 'yellow';
  return 'blue';
}

// ── Colour system ────────────────────────────────────────────────────────────

export const TOKEN: Record<Color, string> = {
  red: '#ea4a4a',
  green: '#2fbb74',
  yellow: '#f2b71c',
  blue: '#3f96f0',
};
const TOKEN_LIGHT: Record<Color, string> = {
  red: '#ff9292',
  green: '#8be9b7',
  yellow: '#ffe17e',
  blue: '#a5d4ff',
};
const TOKEN_DARK: Record<Color, string> = {
  red: '#9c2330',
  green: '#187e4a',
  yellow: '#b07708',
  blue: '#1f5fa8',
};
const TOKEN_EDGE: Record<Color, string> = {
  red: '#5c111c',
  green: '#0d5230',
  yellow: '#6b4305',
  blue: '#12385f',
};

const YARD_TOP: Record<Color, string> = {
  red: 'rgba(234,74,74,0.38)',
  green: 'rgba(47,187,116,0.36)',
  yellow: 'rgba(242,183,28,0.42)',
  blue: 'rgba(63,150,240,0.36)',
};
const YARD_BOTTOM: Record<Color, string> = {
  red: 'rgba(156,35,48,0.55)',
  green: 'rgba(24,126,74,0.55)',
  yellow: 'rgba(176,119,8,0.62)',
  blue: 'rgba(31,95,168,0.55)',
};

const LANE_TOP: Record<Color, string> = {
  red: '#f57878',
  green: '#55cf8e',
  yellow: '#f7c951',
  blue: '#63a8f5',
};
const LANE_BOTTOM: Record<Color, string> = {
  red: '#d44343',
  green: '#2aa96a',
  yellow: '#e0a51f',
  blue: '#357ed6',
};

const CENTER_TINT: Record<Color, string> = {
  red: 'rgba(234,74,74,0.42)',
  green: 'rgba(47,187,116,0.42)',
  yellow: 'rgba(242,183,28,0.48)',
  blue: 'rgba(63,150,240,0.42)',
};